import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, Video, Sparkles, AlertTriangle, Coins } from 'lucide-react';
import api from '../utils/api';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');
  const paymentType = searchParams.get('type');
  const packageId = searchParams.get('package');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await api.get(`/subscription/checkout-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(response.data);
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Payment status error:', error);
      setStatus('error');
    }
  };

  const getPaymentTypeInfo = () => {
    // New credit purchase flow
    if (paymentType === 'credits' || paymentData?.type === 'credit_purchase') {
      const creditsAdded = paymentData?.credits_added || 
        (packageId === 'starter' ? 12 : packageId === 'popular' ? 25 : packageId === 'pro' ? 55 : 0);
      return {
        icon: <Coins className="h-16 w-16 text-amber-500" />,
        title: `${creditsAdded} Credits Added!`,
        description: 'Your credits are ready to use across all AI features including Interview Prep, Career Simulator, and more.',
        buttonText: 'View My Credits',
        buttonAction: () => navigate('/pricing')
      };
    }
    
    // Legacy interview credit
    if (paymentType === 'interview' || paymentData?.type === 'interview_credit') {
      return {
        icon: <Video className="h-16 w-16 text-emerald-600" />,
        title: 'Interview Credit Added!',
        description: 'Your AI Visa Interview session is ready. Practice with our realistic consular officer simulation.',
        buttonText: 'Start Interview',
        buttonAction: () => navigate('/interview/structured')
      };
    }
    
    if (paymentType === 'subscription' || paymentData?.type === 'subscription') {
      return {
        icon: <Sparkles className="h-16 w-16 text-amber-500" />,
        title: 'Welcome to Wayo Premium!',
        description: 'You now have unlimited access to all AI-powered features. Make the most of your admission journey!',
        buttonText: 'Explore Features',
        buttonAction: () => navigate('/dashboard')
      };
    }
    
    return {
      icon: <CheckCircle className="h-16 w-16 text-green-600" />,
      title: 'Payment Successful!',
      description: 'Your payment has been processed successfully.',
      buttonText: 'Continue',
      buttonAction: () => navigate('/dashboard')
    };
  };

  const info = getPaymentTypeInfo();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full shadow-xl">
        <CardContent className="pt-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 text-emerald-600 mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-semibold mb-2">Processing Payment...</h2>
              <p className="text-gray-600">Please wait while we confirm your payment.</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="bg-gradient-to-br from-emerald-100 to-teal-100 p-6 rounded-full w-fit mx-auto mb-6">
                {info.icon}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{info.title}</h2>
              <p className="text-gray-600 mb-8">{info.description}</p>
              <Button 
                onClick={info.buttonAction} 
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-lg py-6"
                data-testid="payment-success-cta"
              >
                {info.buttonText}
              </Button>
            </>
          )}
          
          {(status === 'error' || status === 'timeout' || status === 'expired') && (
            <>
              <div className="bg-amber-100 p-6 rounded-full w-fit mx-auto mb-6">
                <AlertTriangle className="h-16 w-16 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {status === 'expired' ? 'Session Expired' : 'Verification Issue'}
              </h2>
              <p className="text-gray-600 mb-6">
                {status === 'expired' 
                  ? 'Your payment session has expired. Please try again.' 
                  : 'We couldn\'t verify your payment. If you were charged, please contact support.'}
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/interview')} 
                  className="w-full"
                  data-testid="payment-error-retry"
                >
                  Try Again
                </Button>
                <Button 
                  onClick={() => navigate('/contact')} 
                  variant="outline" 
                  className="w-full"
                >
                  Contact Support
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
